import React, { useEffect, useRef, useState } from "react";
import { Building2, MapPin, ShieldCheck, Star, BadgeCheck, Lock, HandCoins } from "lucide-react";
import { api } from "../lib/api";
import { useT } from "../lib/i18n";

const useCountUp = (target, run) => {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!run || !target) return;
    let raf;
    const start = performance.now();
    const dur = 1200;
    const tick = (now) => {
      const p = Math.min((now - start) / dur, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(Math.round(target * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, run]);
  return val;
};

const CONTENT = {
  nl: {
    tag: "Vertrouwd door woningzoekers",
    stats: { listings: "Live woningen", cities: "Steden", checksWeek: "Checks deze week", checks: "Advertenties gecheckt" },
    testiTitle: "Wat onze gebruikers zeggen",
    testimonials: [
      { q: "Binnen een week een bezichtiging én de woning! De matchscore liet meteen zien wat bij me paste.", n: "Sanne", r: "Huurder in Utrecht" },
      { q: "De scam-check heeft me behoed voor een nep-advertentie. Zó waardevol als je snel moet handelen.", n: "Mehmet", r: "Huurder in Rotterdam" },
      { q: "Als makelaar zie ik direct de sterkste kandidaten. Objectief en transparant, precies wat je wil.", n: "Laura", r: "Makelaar, Amsterdam" },
    ],
    trust: [
      { icon: Lock, t: "AVG-proof & versleuteld" },
      { icon: BadgeCheck, t: "Onafhankelijk & transparant" },
      { icon: HandCoins, t: "Geen bemiddelingskosten" },
    ],
  },
  en: {
    tag: "Trusted by home seekers",
    stats: { listings: "Live homes", cities: "Cities", checksWeek: "Checks this week", checks: "Listings checked" },
    testiTitle: "What our users say",
    testimonials: [
      { q: "A viewing and the home within a week! The match score instantly showed what fit me.", n: "Sanne", r: "Tenant in Utrecht" },
      { q: "The scam check saved me from a fake listing. So valuable when you have to act fast.", n: "Mehmet", r: "Tenant in Rotterdam" },
      { q: "As an agent I instantly see the strongest candidates. Objective and transparent — exactly right.", n: "Laura", r: "Agent, Amsterdam" },
    ],
    trust: [
      { icon: Lock, t: "GDPR-proof & encrypted" },
      { icon: BadgeCheck, t: "Independent & transparent" },
      { icon: HandCoins, t: "No brokerage fees" },
    ],
  },
};

export const SocialProof = () => {
  const { lang } = useT();
  const c = CONTENT[lang] || CONTENT.nl;
  const [stats, setStats] = useState(null);
  const [inView, setInView] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    api("/stats/public").then(setStats).catch(() => setStats(null));
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(([e]) => e.isIntersecting && setInView(true), { threshold: 0.2 });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const listings = useCountUp(stats?.live_listings || 0, inView);
  const cities = useCountUp(stats?.cities_covered || 0, inView);
  const checksTotal = useCountUp(stats?.scam_checks_total || 0, inView);

  // Only show stats when all values are real (>0). Otherwise hide to avoid "0+" counter-productive signal.
  const showStats = Boolean(stats && stats.live_listings > 0 && stats.cities_covered > 0 && stats.scam_checks_total > 0);

  const tiles = [
    { icon: Building2, val: listings, label: c.stats.listings, testid: "sp-listings" },
    { icon: MapPin, val: cities, label: c.stats.cities, testid: "sp-cities" },
    { icon: ShieldCheck, val: checksTotal, label: c.stats.checks, testid: "sp-checks" },
  ];

  return (
    <section ref={ref} className="bg-slate-50 py-20" data-testid="social-proof">
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        {showStats && (
          <div className="grid gap-4 sm:grid-cols-3">
            {tiles.map((s) => (
              <div key={s.testid} data-testid={s.testid} className="rounded-3xl border border-border bg-white p-8 text-center shadow-sm">
                <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-brandblue-light text-brandblue">
                  <s.icon size={22} />
                </div>
                <div className="mt-4 font-display text-4xl font-extrabold text-navy">{s.val.toLocaleString(lang === "nl" ? "nl-NL" : "en-US")}+</div>
                <div className="mt-1 text-sm text-slate-500">{s.label}</div>
              </div>
            ))}
          </div>
        )}

        {/* testimonials */}
        <h2 className={`${showStats ? "mt-16" : ""} text-center font-display text-2xl font-extrabold text-navy sm:text-3xl`}>{c.testiTitle}</h2>
        <div className="mt-8 grid gap-5 md:grid-cols-3">
          {c.testimonials.map((t, i) => (
            <div key={i} className="rounded-3xl border border-border bg-white p-6 shadow-sm">
              <div className="flex gap-0.5 text-amber-400">
                {Array.from({ length: 5 }).map((_, k) => <Star key={k} size={16} className="fill-amber-400" />)}
              </div>
              <p className="mt-4 text-slate-700">“{t.q}”</p>
              <div className="mt-4 flex items-center gap-3">
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-navy font-display text-sm font-bold text-white">{t.n[0]}</span>
                <div>
                  <div className="text-sm font-bold text-navy">{t.n}</div>
                  <div className="text-xs text-slate-400">{t.r}</div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* trust chips */}
        <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
          {c.trust.map((b, i) => (
            <span key={i} className="inline-flex items-center gap-2 rounded-full border border-border bg-white px-4 py-2 text-sm font-semibold text-navy shadow-sm">
              <b.icon size={16} className="text-success" /> {b.t}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
};
